import * as React from "react";
import {
  Box,
  Button,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { Project } from "../../types/project";
import LoadingButton from "@mui/lab/LoadingButton";
import { useRescheduleProjectMutation } from "../../services/project.service";
import { DesktopDateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import SelectReason from "../../components/SelectReason";
import { BaseModal } from "../../components/global";
import { useUILabel } from "../../hooks/useUILabel";

interface ModalStartDateFormProps {
  open: boolean;
  onClose: () => void;
  data: Project | null;
}

const ModalStartDateForm = ({
  open,
  onClose,
  data,
}: ModalStartDateFormProps) => {
  const [edit, { isLoading: updating }] = useRescheduleProjectMutation();

  const {
    handleSubmit,
    clearErrors,
    control,
    setValue,
    setError,
    formState: { errors },
  } = useForm();

  // UI Labels
  const editStartTimeLabel = useUILabel("project.editStartTime");
  const startWorkingTimeLabel = useUILabel("project.startWorkingTime");
  const selectRescheduleReasonLabel = useUILabel("project.selectRescheduleReason");
  const cancelLabel = useUILabel("common.cancel");
  const updateLabel = useUILabel("common.update");
  const thisFieldIsRequiredLabel = useUILabel("common.thisFieldIsRequired");
  const invalidLabel = useUILabel("common.invalid");

  const onSubmit = async (payload: any) => {
    try {
      payload.start_date = dayjs(payload.start_date).format("YYYY-MM-DD HH:mm");

      payload.id = data?.id!;
      if (!!payload.reason) payload.reason_schedule = payload.reason;
      await edit(payload).unwrap();
      onClose();
    } catch (err) {
      onClose();
    }
  };

  React.useEffect(() => {
    if (!open) return;

    setValue("start_date", data?.start_date || null);
    clearErrors();
  }, [data, open, setValue, clearErrors]);

  const footer = (
    <>
      <Button onClick={onClose} disabled={updating}>
        {cancelLabel}
      </Button>
      <LoadingButton
        form="start-date-form"
        type="submit"
        variant="contained"
        loading={updating}
      >
        {updateLabel}
      </LoadingButton>
    </>
  );

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={editStartTimeLabel}
      footer={footer}
      maxWidth="sm"
      useGlassCard={true}
    >
      <Box
        component="form"
        id="start-date-form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ p: 3 }}
      >
        <Controller
          name="start_date"
          control={control}
          defaultValue={null}
          rules={{ required: true }}
          render={({ field: { onChange, onBlur, value, ref } }) => (
            <DesktopDateTimePicker
              sx={{ mt: 1, width: "100%", mb: 2 }}
              onChange={onChange}
              value={value ? dayjs(value) : null}
              label={startWorkingTimeLabel}
              minutesStep={5}
              onError={() => {
                setError("start_date", {
                  type: "custom",
                  message: invalidLabel,
                });
              }}
              onAccept={() => clearErrors("start_date")}
              slotProps={{
                textField: {
                  helperText: !!errors.start_date && thisFieldIsRequiredLabel,
                },
              }}
            />
          )}
        />
        <Controller
          name="reason"
          control={control}
          render={({ field: { onChange, value } }) => (
            <SelectReason
              label={selectRescheduleReasonLabel}
              onChange={onChange}
              error={!!errors.reason}
              helperText={!!errors.reason && thisFieldIsRequiredLabel}
              type="reschedule-job"
            />
          )}
        />
      </Box>
    </BaseModal>
  );
};
export default ModalStartDateForm;
